//libs
import React, { memo, useCallback, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { generateProductImpressionData } from '@nykaa/utils/retina/helpers';

//components
import ProductHeader from './components/ProductHeader';
import Badge from './components/Badge';
import Price from './components/Price';
import ImageCarousel from './components/ImageCarousel';
import Image from 'shared/components/ImageV2';
import IOElement from 'shared/components/IOElement';

//constants
import { ASPECT_RATIO, MAX_IMAGE_WIDTH, SOLD_OUT, IMAGE_CARD_CLICKED } from './constants';
import { FALLBACK_ASPECT_RATIO_CLASS, CAROUSEL_CLASS, SoldOut } from './styles';
import { GLOBAL_EVENTS } from 'shared/helpers/analytics/constants';

//styles
import {
  ProductImageContainer,
  OfferMessage,
  Description,
  Title,
  SubTitle,
  SizeSoldout,
  ProductAnchor,
  Tags,
  ImageChildrenContainer,
  ColorOptions,
} from './styles';

//helpers
import { getCleanUrl } from 'shared/helpers/utils/url';
import { storeAnalyticsDataInLocal } from 'shared/helpers/storage/storeData';
import { IProductProps, CAROUSEL_STATES } from './types';
import { transformImages } from './helpers';
import NFAnalytics from 'shared/helpers/analytics/NFAnalytics';
import {
  IMPRESSION_TYPE,
  syncImpressionsHandler,
  transformProductsToBeautyInterface,
  getProductClickedData,
} from 'shared/helpers/utils/impressions';
import { getMergedDataWithRetinaData } from 'shared/helpers/analytics/helpers';
import NFA from 'shared/helpers/analytics/NFAnalytics';

//hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

function Product({
  item,
  className = '',
  onInteraction,
  showPrice = true,
  DescriptionChildren,
  ImageChildren,
  isImageLazy = true,
  containsResponsible = false,
  trackingMetadata = {},
  ProductStamp,
}: IProductProps) {
  const productImages = transformImages(item);
  const {
    actionUrl,
    lcp,
    title,
    subTitle,
    imageUrl,
    tag,
    price,
    discountedPrice,
    discount,
    isOutOfStock,
    messageSizeOutOfStock,
    dynamic_text,
    badge_new,
    token,
  } = item;

  const transformedImpressionsProduct = generateProductImpressionData({
    product: transformProductsToBeautyInterface(item),
    additionalProductParams: {
      productPosition: item?.listingPosition || trackingMetadata?.productPosition,
      listingPosition: trackingMetadata?.widgetPosition,
      listingIdentifier: trackingMetadata?.widgetIdentifier,
      isListingDataRequired: !isEmpty(trackingMetadata),
    },
  });

  const cleanUrl = useMemo(() => {
    return getCleanUrl(actionUrl || '');
  }, [actionUrl]);

  const [carouselState, setCarouselState] = useState(CAROUSEL_STATES.SILENT);
  const [showCarousel, setShowCarousel] = useState(false);

  const productClicked = useCallback(
    (e) => {
      e.preventDefault();
      // data will be accessed via Launch Layer
      //will move this to onInteraction on integration
      if (lcp !== undefined) {
        storeAnalyticsDataInLocal({ lcp });
      }
      onInteraction && onInteraction();
      NFAnalytics.track({
        event: GLOBAL_EVENTS.PRODUCT_CLICK,
        data: getMergedDataWithRetinaData(getProductClickedData(transformedImpressionsProduct)),
      });

      //image card clicked tracking
      NFA.track({
        event: IMAGE_CARD_CLICKED,
      });

      setCarouselState(CAROUSEL_STATES.SILENT);
      window.open(cleanUrl);
    },
    [cleanUrl, lcp, onInteraction, transformedImpressionsProduct]
  );

  const { desktop: showColorOptions } = useRemoteConfig('plpColorOptions');

  const showPLPColorVariants = showColorOptions && item.sibling_colour_codes;
  const carouselTrackingData = {
    productID: item.id,
    productName: item.subTitle,
    productBrandName: item.title,
    MRP: item.price,
    discountedPrice: item.discountedPrice,
    productTags: item.tag,
  };

  const onHover = () => {
    setShowCarousel(true);
    if (carouselState !== CAROUSEL_STATES.AUTOPLAY) setCarouselState(CAROUSEL_STATES.AUTOPLAY);
  };

  const onHoverOut = () => {
    setShowCarousel(false);
    if (carouselState !== CAROUSEL_STATES.SILENT) setCarouselState(CAROUSEL_STATES.SILENT);
  };

  const onProductVisible = () => {
    transformedImpressionsProduct &&
      syncImpressionsHandler([transformedImpressionsProduct], IMPRESSION_TYPE.PRODUCT);
  };

  return (
    <IOElement className={className} options={{ threshold: 0.4 }} onVisible={onProductVisible}>
      <ProductAnchor
        onMouseLeave={onHoverOut}
        onMouseEnter={onHover}
        aria-labelledby={`aria-label-${item.id}-1 aria-label-${item.id}-2`}
        href={cleanUrl}
        data-test="product-anchor"
        onClick={productClicked}
        target="_blank"
        data-at="product"
        carouselState={carouselState}
        containsResponsible={containsResponsible && isEmpty(token)}
      >
        <ProductHeader headerDetails={token} />
        <ProductImageContainer>
          {!isEmpty(ImageChildren) && (
            <ImageChildrenContainer>{ImageChildren}</ImageChildrenContainer>
          )}

          <Image
            src={imageUrl}
            width={MAX_IMAGE_WIDTH}
            aspectRatio={ASPECT_RATIO}
            lazy={isImageLazy}
            alt={`${title} - ${subTitle}`}
            data-test="product-img"
            data-at="product-img"
          />

          {showCarousel && productImages.length > 1 && (
            <ImageCarousel
              images={productImages}
              carouselState={carouselState}
              setCarouselState={setCarouselState}
              isImageLazy
              className={`${CAROUSEL_CLASS} ${FALLBACK_ASPECT_RATIO_CLASS}`}
              width={MAX_IMAGE_WIDTH}
              alt={`${title} - ${subTitle}`}
              aspectRatio={ASPECT_RATIO}
              productId={item.id}
              carouselTrackingData={carouselTrackingData}
              data-at="product-img-carousel"
            />
          )}

          <Badge badgeData={badge_new} data-at="product-badge" />
        </ProductImageContainer>

        {ProductStamp && ProductStamp}

        <Description>
          {Boolean(isOutOfStock) && (
            <SoldOut data-test="product-soldout" data-at="product-soldout">
              {SOLD_OUT}
            </SoldOut>
          )}
          {isEmpty(tag) === false && (
            <Tags
              tagData={tag}
              className="product-tag"
              data-at="product-tag"
              data-test="product-tag"
            />
          )}
          <Title id={`aria-label-${item.id}-1`} data-at="product-title">{title}</Title>
          <SubTitle id={`aria-label-${item.id}-2`} data-at="product-subtitle">{subTitle}</SubTitle>

          {showPLPColorVariants && (
            <ColorOptions colors={item.sibling_colour_codes} data-at="color-options" />
          )}

          {showPrice && (
            <Price
              discount={discount}
              discountedPrice={discountedPrice}
              price={price}
              data-at="product-price"
            />
          )}

          {DescriptionChildren !== undefined && DescriptionChildren}

          {!isEmpty(dynamic_text) && (
            <OfferMessage data-test="product-offer" data-at="product-offer">
              {dynamic_text?.title}
            </OfferMessage>
          )}

          {Boolean(isOutOfStock) && messageSizeOutOfStock && (
            <SizeSoldout data-at="product-soldout">{messageSizeOutOfStock}</SizeSoldout>
          )}
        </Description>
      </ProductAnchor>
    </IOElement>
  );
}
export default memo(Product);
